import { useEffect, useRef, useState, type RefObject } from "react";
import type { Map as MapboxMap, MapMouseEvent } from "mapbox-gl";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveBar } from "@nivo/bar";
import { PLACE_LEVELS } from "@/constants";
import { getPlacePriceDistribution } from "../../repo/finance";
import "./Finance.css";
import { getEnvParam, type TileEnv } from "@/env";

interface FinancePlacesViewProps {
    map: RefObject<MapboxMap | undefined>;
    env: TileEnv;
}

function formatPrice(value: number | null | undefined): string {
    if (value == null) return "—";
    if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `£${Math.round(value / 1000)}k`;
    return `£${Math.round(value)}`;
}

function mountLayer(map: MapboxMap, level: number) {
    if (map.getSource("mvt-tiles")) {
        if (map.getLayer("tiles")) map.removeLayer("tiles");
        if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
        map.removeSource("mvt-tiles");
    }

    map.addSource("mvt-tiles", {
        type: "vector",
        tiles: [
            `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/finance/places/${level}/{z}/{x}/{y}?${getEnvParam()}`,
        ],
    });

    map.addLayer({
        id: "tiles",
        type: "fill",
        source: "mvt-tiles",
        "source-layer": "finance_places",
        paint: {
            "fill-color": [
                "interpolate",
                ["linear"],
                ["get", "avg_price"],
                100_000,
                "#2ecc71",
                400_000,
                "#f1c40f",
                1_000_000,
                "#e67e22",
                2_000_000,
                "#e74c3c",
            ],
            "fill-opacity": 0.55,
            "fill-outline-color": "rgba(255, 255, 255, 0.5)",
        },
    });
}

function unmount(map: MapboxMap) {
    if (map.getSource("mvt-tiles")) {
        if (map.getLayer("tiles")) map.removeLayer("tiles");
        if (map.getLayer("tiles-values")) map.removeLayer("tiles-values");
        map.removeSource("mvt-tiles");
    }
}

interface PlaceClick {
    id: number;
    name?: string;
}

function FinancePlacesView(props: FinancePlacesViewProps) {
    const [level, setLevel] = useState<number>(PLACE_LEVELS[0]?.value ?? 2);
    const [selected, setSelected] = useState<PlaceClick>();
    const clickHandlerRef = useRef<((e: MapMouseEvent) => void) | null>(null);

    useEffect(() => {
        const map = props.map.current;
        if (!map) return;
        mountLayer(map, level);

        const handler = (e: MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ["tiles"],
            });
            const f = features[0];
            if (!f) return;
            const id = Number(f.id ?? f.properties?.id);
            if (Number.isNaN(id)) return;
            setSelected({ id, name: f.properties?.name as string | undefined });
        };
        clickHandlerRef.current = handler;
        map.on("click", "tiles", handler);
        map.getCanvas().style.cursor = "pointer";

        return () => {
            if (clickHandlerRef.current) {
                map.off("click", "tiles", clickHandlerRef.current);
                clickHandlerRef.current = null;
            }
            map.getCanvas().style.cursor = "";
            unmount(map);
        };
    }, [level, props.env]);

    if (selected) {
        return (
            <PlaceDetail
                placeId={selected.id}
                placeName={selected.name}
                onClose={() => setSelected(undefined)}
            />
        );
    }

    return (
        <div className="finance-panel">
            <div className="finance-level-select">
                <label htmlFor="finance-level">Level</label>
                <select
                    id="finance-level"
                    value={level}
                    onChange={(e) => setLevel(Number(e.target.value))}
                >
                    {PLACE_LEVELS.map((l) => (
                        <option key={l.value} value={l.value}>
                            {l.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="finance-panel__note">
                Click any polygon to open its full price distribution.
            </div>
        </div>
    );
}

interface PlaceDetailProps {
    placeId: number;
    placeName?: string;
    onClose(): void;
}

function PlaceDetail(props: PlaceDetailProps) {
    const query = useQuery({
        queryKey: ["place-distribution", props.placeId],
        queryFn: () => getPlacePriceDistribution(props.placeId),
    });

    return (
        <div className="finance-place-detail">
            <div className="finance-place-detail__header">
                <button
                    type="button"
                    className="finance-place-detail__close"
                    onClick={props.onClose}
                >
                    ←
                </button>
                <div>
                    <div style={{ fontWeight: "bold" }}>
                        {query.data?.place_name ?? props.placeName ?? "Place"}
                    </div>
                    <div style={{ fontSize: 11, color: "#ffffff99" }}>
                        ID: {props.placeId}
                    </div>
                </div>
            </div>

            {query.isPending && <div>Loading…</div>}
            {query.error && (
                <div style={{ color: "#e74c3c" }}>
                    {(query.error as Error).message}
                </div>
            )}

            {query.data && (
                <>
                    <dl>
                        <dt>Average price</dt>
                        <dd>{formatPrice(query.data.overall_average)}</dd>
                        <dt>Median price</dt>
                        <dd>{formatPrice(query.data.overall_median)}</dd>
                        <dt>Total sales</dt>
                        <dd>
                            {Math.round(
                                query.data.total_sales,
                            ).toLocaleString()}
                        </dd>
                    </dl>

                    <div>
                        <div
                            style={{
                                fontSize: 12,
                                color: "#ffffff99",
                                marginBottom: 4,
                            }}
                        >
                            Price distribution
                        </div>
                        <div style={{ height: 220 }}>
                            <ResponsiveBar
                                data={query.data.histogram.map((bin, i) => ({
                                    binLabel: formatPrice(bin.min),
                                    count: bin.count,
                                    min: bin.min,
                                    max: bin.max,
                                    index: i,
                                }))}
                                keys={["count"]}
                                indexBy="binLabel"
                                margin={{
                                    top: 10,
                                    right: 10,
                                    bottom: 60,
                                    left: 40,
                                }}
                                padding={0.1}
                                colors={"#e5833e55"}
                                enableLabel={false}
                                axisBottom={{
                                    tickSize: 4,
                                    tickPadding: 4,
                                    tickRotation: -45,
                                }}
                                axisLeft={{
                                    tickSize: 4,
                                    tickPadding: 4,
                                }}
                                borderColor="#e5833e"
                                borderWidth={1}
                                theme={{
                                    grid: {
                                        line: {
                                            stroke: "#ffffff11",
                                            strokeWidth: 1,
                                        },
                                    },
                                    axis: {
                                        ticks: { text: { fill: "#ffffffaa" } },
                                        legend: { text: { fill: "#8888ff" } },
                                    },
                                }}
                                tooltip={({ data, value }) => (
                                    <div
                                        style={{
                                            padding: 8,
                                            color: "#fff",
                                            background: "#222",
                                            borderRadius: 2,
                                            fontSize: 12,
                                        }}
                                    >
                                        {formatPrice(data.min)}–
                                        {formatPrice(data.max)}
                                        <br />
                                        <strong>{value}</strong> sales
                                    </div>
                                )}
                                role="application"
                                ariaLabel="Price distribution"
                            />
                        </div>
                    </div>

                    <div>
                        <div
                            style={{
                                fontSize: 12,
                                color: "#ffffff99",
                                marginBottom: 4,
                            }}
                        >
                            By property type
                        </div>
                        <table>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Avg</th>
                                    <th>Median</th>
                                    <th>Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {query.data.by_property_type.map((row) => (
                                    <tr key={row.property_type}>
                                        <td>{row.property_type}</td>
                                        <td>
                                            {formatPrice(row.average_price)}
                                        </td>
                                        <td>{formatPrice(row.median_price)}</td>
                                        <td>
                                            {Math.round(
                                                row.count,
                                            ).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default FinancePlacesView;
